// @ts-nocheck
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { calculateDistanceToWall, zeroOneBound } from '../../util/compass';
import {
  CompassChartData,
  CompassSymbolData,
  StrategyCompassMode,
  CompassHistoryRsp,
  CompassSymHistoryFields,
} from '../../types/compass';
import {
  CompassDataResponse,
  ErrorResponse,
  EquityFieldKey,
} from '../../types';
import { dayjs, isErrorResponse } from '../../util';
import useToast from '../useToast';
import useLog from '../useLog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { unpricedEquitiesState } from '../../states';
import { CompassParams } from '../../types/compass';
import { LegacyEquity } from '../../types/equityhub';
export const MAX_COMPASS_SYMS = 75;

type useCompassDataProps = {
  compassParams: CompassParams;
  hiddenSymbols: Set<string>;
  historyLookback: number;
};

export type CompassData = {
  loading: boolean;
  fetchData: (syms: string[]) => Promise<void>;
  data: Map<string, CompassSymbolData>;
  chartData: CompassChartData[];
  visibleChartData: CompassChartData[];
  setVisibleChartData: (visibleChartData: CompassChartData[]) => void;
  setChartData: (chartData: CompassChartData[]) => void;
  compassHistoryFromSym?: Record<string, CompassChartData[]>;
};

const compassHistFieldFromFieldName: Record<string, CompassSymHistoryFields> = {
  ivRank: 'iv_rank',
  skewRank: 'skew_rank',
};

export const useCompassData = ({
  compassParams,
  hiddenSymbols,
  historyLookback,
}: useCompassDataProps): CompassData => {
  const { mode, xAxisDataKey, yAxisDataKey, zAxisDataKey, syms } =
    compassParams;

  const [loading, setLoading] = useState(false);
  const [fetchedDataArr, setFetchedDataArr] = useState<CompassDataResponse[]>(
    [],
  );
  const equities = useRecoilValue(unpricedEquitiesState);
  const [chartDataState, setChartDataState] = useState<CompassChartData[]>([]);
  const [visibleChartDataState, setVisibleChartDataState] = useState<
    CompassChartData[]
  >([]);

  const { openToast } = useToast();
  const { fetchAPIWithLog } = useLog('StrategyCompass');

  useEffect(() => {
    setFetchedDataArr((oldArr) => oldArr.filter((d) => syms.includes(d.sym)));
  }, [syms]);

  const fetchData = useCallback(
    async (newSyms: string[] = []) => {
      if (newSyms.length === 0) {
        return;
      } else if (newSyms.length + syms.length > MAX_COMPASS_SYMS) {
        const remaining = MAX_COMPASS_SYMS - syms.length;
        openToast({
          type: 'warning',
          message: `You can only display up to ${MAX_COMPASS_SYMS} symbols at once.
        Please delete some symbols using the settings icon in the toolbar above before adding more symbols.`,
        });
        if (remaining <= 0) {
          return;
        }

        newSyms = newSyms.slice(0, remaining);
      }

      setLoading(true);
      const fetchedData = (await fetchAPIWithLog(
        `v1/compass?syms=${encodeURIComponent(newSyms.join(','))}&x=${
          mode === StrategyCompassMode.Compass ? 'price' : 'skew'
        }`,
      )) as CompassDataResponse[] | ErrorResponse;
      setLoading(false);

      if (
        isErrorResponse(fetchedData) ||
        !Array.isArray(fetchedData) ||
        fetchedData.length === 0
      ) {
        openToast({
          message:
            'There was an error fetching compass data. Please try again.',
          type: 'error',
        });
        return;
      }
      setFetchedDataArr((oldArr) => [...oldArr, ...fetchedData]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equities, mode, syms],
  );

  const data = useMemo(() => {
    return new Map(
      fetchedDataArr.map((symData) => {
        const sym = symData.sym;
        const equity = equities.get(sym);
        const price = parseFloat(symData.price);

        let compassSymData: CompassSymbolData = {
          price,
          sym,
          bollingerBand: symData.bollingerBand,
          rsi: parseFloat(symData.rsi) / 100,
        };

        if (equity != null) {
          compassSymData = {
            ...compassSymData,
            equity: equity as LegacyEquity,
            ivPct: equity.iv_pct,
            ivRank: equity.iv_rank,
            rrPercentile: equity.skew_rank, // skew_rank from backend is actually risk reversal percentile
            callSkewPercentile: equity.cskew_pct,
            putSkewPercentile: equity.pskew_pct,
            proximityToCallWall: calculateDistanceToWall(
              price,
              (equity as LegacyEquity).cws,
            ),
            proximityToPutWall: calculateDistanceToWall(
              price,
              (equity as LegacyEquity).pws,
            ),
            nextExpCallVolumePercent: equity[EquityFieldKey.neCallVolume],
            nextExpPutVolumePercent: equity[EquityFieldKey.nePutVolume],
            nextExpDeltaPercent: equity[EquityFieldKey.nextExpDelta],
            nextExpGammaPercent: equity[EquityFieldKey.nextExpGamma],
            garchRank: equity[EquityFieldKey.garchRank],
            skewRank: equity[EquityFieldKey.skewRank],
            oneMonthIv: equity[EquityFieldKey.iv30],
            oneMonthRv: equity[EquityFieldKey.rv30],
          };
        }

        return [sym, compassSymData];
      }),
    );
  }, [fetchedDataArr, equities]);

  // Generate chart data whenever dependencies change
  useEffect(() => {
    const newChartData = [...data.keys()].flatMap((sym) => {
      const symData = data.get(sym)!;
      const unroundedY = symData[yAxisDataKey]!;
      const unroundedX = symData[xAxisDataKey]!;
      const unroundedZ =
        zAxisDataKey == null ? undefined : symData[zAxisDataKey]!;
      if (unroundedY == null || unroundedX == null) {
        return [];
      }

      return [
        {
          x: zeroOneBound(unroundedX)!,
          y: zeroOneBound(unroundedY)!,
          z: unroundedZ == null ? undefined : zeroOneBound(unroundedZ)!,
          unroundedX,
          unroundedY,
          unroundedZ,
          sym,
          symData,
          hidden: hiddenSymbols.has(sym),
        },
      ];
    });

    setChartDataState(newChartData);
  }, [data, mode, xAxisDataKey, yAxisDataKey, zAxisDataKey, hiddenSymbols]);

  const { data: compassHistory } = useFetchCompassHistory({
    syms,
    mode,
    lookback: historyLookback,
  });

  const compassHistoryFromSym = useMemo(() => {
    if (compassHistory == null) {
      return;
    }
    return Object.fromEntries(
      Object.entries(compassHistory).map(([sym, symHistAry]) => [
        sym,
        symHistAry
          .map((symHist) => {
            const xField =
              compassHistFieldFromFieldName[xAxisDataKey] ?? xAxisDataKey;
            const yField =
              compassHistFieldFromFieldName[yAxisDataKey] ?? yAxisDataKey;
            const zField =
              compassHistFieldFromFieldName[zAxisDataKey ?? ''] ?? zAxisDataKey;

            const unroundedY = symHist[yField] as number;
            const unroundedX = symHist[xField] as number;
            const unroundedZ =
              zField == null ? undefined : (symHist[zField] as number);
            if (unroundedY == null || unroundedX == null) {
              return;
            }
            const date = dayjs(symHist.trade_date).utc();
            return {
              ...symHist,
              date,
              price: symHist.close,
              x: zeroOneBound(unroundedX)!,
              y: zeroOneBound(unroundedY)!,
              z: unroundedZ == null ? undefined : zeroOneBound(unroundedZ)!,
              unroundedX,
              unroundedY,
              unroundedZ,
              sym,
            };
          })
          .filter(Boolean),
      ]),
    );
  }, [compassHistory, xAxisDataKey, yAxisDataKey, zAxisDataKey]);

  // Update visible chart data whenever chart data changes
  useEffect(() => {
    setVisibleChartDataState(chartDataState.filter((d) => !d.hidden));
  }, [chartDataState]);

  const setChartData = useCallback((newChartData: CompassChartData[]) => {
    setChartDataState(newChartData);
  }, []);

  const setVisibleChartData = useCallback(
    (newVisibleChartData: CompassChartData[]) => {
      setVisibleChartDataState(newVisibleChartData);
    },
    [],
  );

  return {
    loading,
    fetchData,
    data,
    chartData: chartDataState,
    visibleChartData: visibleChartDataState,
    setVisibleChartData,
    setChartData,
    compassHistoryFromSym,
  };
};

export const useFetchCompassHistory = <
  TData = CompassHistoryRsp,
  TError = ErrorResponse,
>(
  {
    syms,
    mode,
    lookback,
  }: {
    syms: string[];
    mode: StrategyCompassMode;
    lookback: number;
  },
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>,
) => {
  const { fetchAPIWithLog } = useLog('StrategyCompass');
  const { openToast } = useToast();
  return useQuery<TData, TError>({
    queryKey: ['useFetchCompassHistory', { syms, mode, lookback }],
    queryFn: async (): Promise<TData> => {
      if (syms.length > MAX_COMPASS_SYMS) {
        openToast({
          type: 'warning',
          message: `You can only display up to ${MAX_COMPASS_SYMS} symbols at once.
            Please delete some symbols using the settings icon in the toolbar above before adding more symbols.`,
        });
      }

      const fetchedData = (await fetchAPIWithLog(
        `v1/compass_hist?syms=${encodeURIComponent(syms.slice(0, MAX_COMPASS_SYMS).join(','))}&lookback=${lookback}`,
      )) as CompassDataResponse[] | ErrorResponse;

      if (isErrorResponse(fetchedData)) {
        openToast({
          message:
            'There was an error fetching compass data. Please try again.',
          type: 'error',
        });
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw fetchedData;
      }
      return fetchedData;
    },
    ...options,
    enabled: !!syms.length && (options?.enabled ?? true),
  });
};
